import React from 'react'

// The active heading is the last one whose top has crossed the scan line (a
// reading position just below the sticky navbar), so exactly one item is active
// while scrolling — an IntersectionObserver alone loses track on sections
// taller than the viewport.
//
// `headings` must be referentially stable across renders, otherwise the scroll
// listener is torn down and re-subscribed on every render.
export function useActiveHeadingId(
  headings: readonly { id: string }[],
  scanLineOffset: number,
  scrollContainer?: HTMLElement | null
): string | null {
  const [activeId, setActiveId] = React.useState<string | null>(
    headings[0]?.id ?? null
  )

  React.useEffect(() => {
    let frame = 0
    const update = () => {
      // Once the page can scroll no further, the last sections can never reach
      // the scan line, so the final heading takes over at the bottom.
      const scroller = scrollContainer ?? document.documentElement
      const isAtBottom =
        scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - 1
      const lastHeading = headings[headings.length - 1]
      if (isAtBottom && lastHeading) {
        setActiveId(lastHeading.id)
        return
      }
      let current: string | null = headings[0]?.id ?? null
      for (const heading of headings) {
        const element = document.getElementById(heading.id)
        if (element && element.getBoundingClientRect().top <= scanLineOffset) {
          current = heading.id
        }
      }
      setActiveId(current)
    }
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(update)
    }
    update()
    const scroller = scrollContainer ?? window
    scroller.addEventListener('scroll', schedule, { passive: true })
    window.addEventListener('resize', schedule, { passive: true })
    return () => {
      cancelAnimationFrame(frame)
      scroller.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [headings, scanLineOffset, scrollContainer])

  return activeId
}
